import React, { useState, useEffect, useMemo } from 'react';
import { Users, Search, Check, ChevronDown, Eye, Edit2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { FileItem, FileShareRecord, SharePermission } from '../types';
import { SupabaseFileStorage } from '../utils/supabaseFileStorage';
import { fetchWorkspaceMembers, WorkspaceMemberWithProfile } from '@/lib/workspaceMembers';
import { toast } from 'sonner';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: FileItem | null;
  workspaceId: string;
  currentUserId: string;
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  member: 'Member',
  client: 'Client',
};

const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-purple-500/15 text-purple-400',
  admin: 'bg-red-500/15 text-red-400',
  manager: 'bg-orange-500/15 text-orange-400',
  member: 'bg-blue-500/15 text-blue-400',
  client: 'bg-emerald-500/15 text-emerald-400',
};

function Avatar({ name, avatar, size = 32 }: { name: string; avatar?: string; size?: number }) {
  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return (
    <div
      className="rounded-full bg-workspace-accent/20 flex items-center justify-center overflow-hidden flex-shrink-0 text-workspace-accent font-semibold"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {avatar ? (
        <img src={avatar} alt={name} className="w-full h-full object-cover" />
      ) : (
        initials
      )}
    </div>
  );
}

function PermissionBadge({
  value,
  onChange,
}: {
  value: SharePermission;
  onChange: (p: SharePermission) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border bg-muted hover:bg-accent text-xs font-medium text-foreground transition-colors"
      >
        {value === 'view' ? <Eye size={12} /> : <Edit2 size={12} />}
        {value === 'view' ? 'Can View' : 'Can Edit'}
        <ChevronDown size={10} className="text-muted-foreground" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-popover border border-border rounded-lg shadow-xl overflow-hidden w-36">
            {(['view', 'edit'] as SharePermission[]).map(p => (
              <button
                key={p}
                onClick={() => { onChange(p); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-accent transition-colors ${value === p ? 'text-workspace-accent font-semibold' : 'text-foreground'}`}
              >
                {p === 'view' ? <Eye size={12} /> : <Edit2 size={12} />}
                {p === 'view' ? 'Can View' : 'Can Edit'}
                {value === p && <Check size={10} className="ml-auto" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FilterDropdown({
  value,
  onChange,
}: {
  value: 'all' | 'members' | 'clients';
  onChange: (v: 'all' | 'members' | 'clients') => void;
}) {
  const [open, setOpen] = useState(false);
  const labels = {
    all: 'All',
    members: 'Members',
    clients: 'Clients'
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-2 h-7 rounded-sm hover:bg-accent text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {labels[value]}
        <ChevronDown size={10} className="text-muted-foreground" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-popover border border-border rounded-lg shadow-xl overflow-hidden w-28">
            {(['all', 'members', 'clients'] as const).map(f => (
              <button
                key={f}
                onClick={() => { onChange(f); setOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-accent transition-colors ${value === f ? 'text-workspace-accent font-semibold' : 'text-foreground'}`}
              >
                {labels[f]}
                {value === f && <Check size={10} className="ml-auto" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  file,
  workspaceId,
  currentUserId,
}) => {
  const [search, setSearch] = useState('');
  const [members, setMembers] = useState<WorkspaceMemberWithProfile[]>([]);
  const [existingShares, setExistingShares] = useState<FileShareRecord[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [defaultPermission, setDefaultPermission] = useState<SharePermission>('view');
  const [perUserPermission, setPerUserPermission] = useState<Map<string, SharePermission>>(new Map());
  const [filterType, setFilterType] = useState<'all' | 'members' | 'clients'>('all');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load members and existing shares whenever modal opens
  useEffect(() => {
    if (!isOpen || !file) return;
    setSearch('');
    setFilterType('all');
    setSelectedIds(new Set());
    setPerUserPermission(new Map());
    setDefaultPermission('view');

    const load = async () => {
      setLoading(true);
      const [allMembers, shares] = await Promise.all([
        fetchWorkspaceMembers(workspaceId),
        SupabaseFileStorage.getFileShares(file.id),
      ]);
      // Exclude current user and workspace owner (owner always has access)
      setMembers(allMembers.filter(m => m.id !== currentUserId && m.role !== 'owner'));
      setExistingShares(shares);
      
      const initialSelected = new Set<string>();
      const initialPerms = new Map<string, SharePermission>();
      shares.forEach(s => {
        initialSelected.add(s.sharedWithUserId);
        initialPerms.set(s.sharedWithUserId, s.permission);
      });
      setSelectedIds(initialSelected);
      setPerUserPermission(initialPerms);
      
      setLoading(false);
    };
    load();
  }, [isOpen, file, workspaceId, currentUserId]);

  // Split members into team & clients
  const teamMembers = useMemo(() => members.filter(m => m.role !== 'client'), [members]);
  const clients = useMemo(() => members.filter(m => m.role === 'client'), [members]);

  const filtered = (list: WorkspaceMemberWithProfile[]) =>
    list.filter(m =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.email.toLowerCase().includes(search.toLowerCase())
    );

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = (list: WorkspaceMemberWithProfile[]) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      list.forEach(m => next.add(m.id));
      return next;
    });
  };

  const deselectAll = (list: WorkspaceMemberWithProfile[]) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      list.forEach(m => next.delete(m.id));
      return next;
    });
  };

  const handleShare = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const currentlySharedIds = new Set(existingShares.map(s => s.sharedWithUserId));
      
      const idsToShare = new Set<string>();
      const idsToUpdatePerm = new Set<string>();
      
      for (const uid of selectedIds) {
        if (!currentlySharedIds.has(uid)) {
          idsToShare.add(uid);
        } else {
          // Check if permission changed
          const oldPerm = existingShares.find(s => s.sharedWithUserId === uid)?.permission;
          const newPerm = perUserPermission.get(uid) ?? defaultPermission;
          if (oldPerm !== newPerm) {
            idsToUpdatePerm.add(uid);
          }
        }
      }

      const idsToUnshare = new Set<string>();
      for (const uid of currentlySharedIds) {
        if (!selectedIds.has(uid)) {
          idsToUnshare.add(uid);
        }
      }

      // 1. Unshare
      if (idsToUnshare.size > 0) {
        await SupabaseFileStorage.unshareFile(file.id, file.type, Array.from(idsToUnshare));
      }

      // 2. Share new
      const grouped = new Map<SharePermission, string[]>();
      for (const uid of idsToShare) {
        const perm = perUserPermission.get(uid) ?? defaultPermission;
        const arr = grouped.get(perm) ?? [];
        arr.push(uid);
        grouped.set(perm, arr);
      }
      for (const [perm, uids] of grouped) {
        await SupabaseFileStorage.shareFile(file.id, file.type, uids, perm, currentUserId);
      }

      // 3. Update permissions
      for (const uid of idsToUpdatePerm) {
        const perm = perUserPermission.get(uid) ?? defaultPermission;
        await SupabaseFileStorage.updateSharePermission(file.id, uid, perm);
      }

      toast.success(`Share settings updated for "${file.name}"`);
      // Refresh existing shares
      const updated = await SupabaseFileStorage.getFileShares(file.id);
      setExistingShares(updated);
      
      if (idsToShare.size === 0 && idsToUpdatePerm.size === 0 && idsToUnshare.size === 0) {
          // No changes were made, close directly
          onClose();
      } else {
          onClose();
      }
    } catch {
      toast.error('Failed to update share settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const MemberRow = ({ member }: { member: WorkspaceMemberWithProfile }) => {
    const isSelected = selectedIds.has(member.id);
    const perm = perUserPermission.get(member.id) ?? defaultPermission;

    return (
      <div
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors cursor-pointer group border border-transparent ${
          isSelected
            ? 'bg-workspace-accent/10'
            : 'hover:bg-accent'
        }`}
        onClick={() => toggleSelect(member.id)}
      >
        <Checkbox 
          checked={isSelected}
          onCheckedChange={() => toggleSelect(member.id)}
          onClick={e => e.stopPropagation()}
          className="mr-1"
        />
        <div className="relative flex-shrink-0">
          <Avatar name={member.name} avatar={member.avatar} size={34} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-foreground truncate">{member.name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wider ${ROLE_COLORS[member.role] ?? 'bg-muted text-muted-foreground'}`}>
              {ROLE_LABELS[member.role] ?? member.role}
            </span>
          </div>
          <p className="text-xs text-muted-foreground truncate">{member.email}</p>
        </div>
        {isSelected && (
          <div onClick={e => e.stopPropagation()}>
            <PermissionBadge
              value={perm}
              onChange={p => {
                setPerUserPermission(prev => new Map(prev).set(member.id, p));
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const SectionHeader = ({
    label,
    count,
    list,
  }: {
    label: string;
    count: number;
    list: WorkspaceMemberWithProfile[];
  }) => {
    const allSelected = list.length > 0 && list.every(m => selectedIds.has(m.id));
    return (
      <div className="flex items-center justify-between px-4 py-2 sticky top-0 bg-card z-10 border-b border-border">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label} <span className="text-foreground">({count})</span>
        </span>
        {list.length > 0 && (
          <button
            onClick={() => allSelected ? deselectAll(list) : selectAll(list)}
            className="text-xs text-workspace-accent hover:underline font-medium"
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        )}
      </div>
    );
  };

  const fileIcon = file?.type === 'folder' ? '📁' : '📄';

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-lg bg-card border-border p-0 overflow-hidden gap-0">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{fileIcon}</span>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base font-semibold text-foreground truncate">
                Share "{file?.name}"
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {file?.type === 'folder'
                  ? 'All files inside this folder will also be shared'
                  : 'Choose who can access this file'}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-col max-h-[70vh]">
          {/* Default permission + search */}
          <div className="px-4 pt-4 pb-2 space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1 flex items-center">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={`Search ${filterType === 'all' ? 'members & clients' : filterType}...`}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 pr-28 h-9 text-sm bg-background border-input w-full"
                />
                <div className="absolute right-1">
                  <FilterDropdown value={filterType} onChange={setFilterType} />
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground flex-shrink-0">
                <PermissionBadge value={defaultPermission} onChange={setDefaultPermission} />
              </div>
            </div>
          </div>

          {/* Removed Existing Shares section for unified list */}

          {/* Member list */}
          <ScrollArea className="flex-1 min-h-0">
            {loading ? (
              <div className="flex flex-col gap-2 p-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="w-8 h-8 rounded-full bg-muted animate-pulse flex-shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-32 bg-muted animate-pulse rounded" />
                      <div className="h-2.5 w-48 bg-muted animate-pulse rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="pb-4">
                {/* Team Members */}
                {(filterType === 'all' || filterType === 'members') && filtered(teamMembers).length > 0 && (
                  <>
                    <SectionHeader label="Team Members" count={filtered(teamMembers).length} list={teamMembers} />
                    <div className="pt-1 pb-2 px-2 space-y-0.5">
                      {filtered(teamMembers).map(m => <MemberRow key={m.id} member={m} />)}
                    </div>
                  </>
                )}

                {/* Clients */}
                {(filterType === 'all' || filterType === 'clients') && filtered(clients).length > 0 && (
                  <>
                    <SectionHeader label="Clients" count={filtered(clients).length} list={clients} />
                    <div className="pt-1 pb-2 px-2 space-y-0.5">
                      {filtered(clients).map(m => <MemberRow key={m.id} member={m} />)}
                    </div>
                  </>
                )}

                {!(((filterType === 'all' || filterType === 'members') && filtered(teamMembers).length > 0) || 
                   ((filterType === 'all' || filterType === 'clients') && filtered(clients).length > 0)) && (
                  <div className="flex flex-col items-center justify-center py-10 text-center px-6">
                    <Users className="w-8 h-8 text-muted-foreground mb-3 opacity-40" />
                    <p className="text-sm font-medium text-foreground">No members found</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {search ? 'Try a different search term' : 'No other workspace members to share with'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-muted/20 flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {selectedIds.size > 0
                ? `${selectedIds.size} ${selectedIds.size === 1 ? 'person' : 'people'} selected`
                : 'Select people to share with'}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="h-9 px-4 text-sm">
                Cancel
              </Button>
              <Button
                onClick={handleShare}
                disabled={saving}
                className="h-9 px-5 text-sm bg-workspace-accent hover:bg-workspace-accent-hover text-white"
              >
                {saving ? 'Saving...' : `Save changes`}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
